/**
 * @file Public landing page — pages/Landing/FAQPage.tsx.
 */

import React from "react";
import { Link } from "react-router-dom";
import "./LandingPage.css";
import "./ContentPage.css";

const FAQPage: React.FC = () => {
  return (
    <div className="landing-root">
      <header className="landing-header flex justify-center items-center space-x-3 text-center py-4">
        <h1 className="landing-main-title text-4xl font-bold text-primary">
          ICoMEC
        </h1>
        <h2 className="landing-sub-title text-xl text-grayDark">
          Indian Construction Materials Embodied Carbon Data Base
        </h2>
      </header>

      <nav className="landing-nav">
        <Link to="/">Home</Link>
        <Link to="/join">Join</Link>
        <Link to="/impact">Impact</Link>
        <Link to="/research">Research and Analysis</Link>
        <Link to="/about">About</Link>
      </nav>

      <section className="content-page">
        <h1 className="content-title">FAQ</h1>
        
        <div className="faq-section">
          <h3>General Platform Information</h3>
          
          <div className="faq-item">
            <div className="faq-question">Do I need prior knowledge of Life Cycle Assessment (LCA) to use ICoMEC?</div>
            <div className="faq-answer">
              The platform is designed to be intuitive and does not require prior LCA expertise. For users unfamiliar with technical terms, we recommend reviewing the platform Glossary to understand the basic terminology used in our reports.
            </div>
          </div>

          <div className="faq-item">
            <div className="faq-question">Why is an India-specific database necessary?</div>
            <div className="faq-answer">
              India possesses significant regional variations in raw materials, localized manufacturing technologies, and supply chain structures. ICoMEC provides data built specifically for the Indian context rather than relying on estimates derived from other geographic regions.
            </div>
          </div>

          <div className="faq-item">
            <div className="faq-question">What materials are currently covered?</div>
            <div className="faq-answer">
              The platform currently includes the four most carbon-intensive construction materials: Cement, Glass, Steel, and Bricks. We are expanding our scope to include various walling materials and finishes in future updates.
            </div>
          </div>

          <div className="faq-item">
            <div className="faq-question">What is the source of the data?</div>
            <div className="faq-answer">
              Current datasets are collated from secondary sources, including scientific literature and environmental disclosures. ICoMEC is actively growing, and industry stakeholders are encouraged to contribute primary data to the database.
            </div>
          </div>
        </div>

        <div className="faq-section">
          <h3>Data Quality and Specificity</h3>
          
          <div className="faq-item">
            <div className="faq-question">What is meant by "Level of Detail"?</div>
            <div className="faq-answer">
              This refers to the combination of transparency (availability of metadata) and granularity (disaggregated data for specific materials, fuels, and energy). ICoMEC accepts datasets with varying levels of detail based on what the contributor has available.
            </div>
          </div>

          <div className="faq-item">
            <div className="faq-question">What defines "Reliable Data"?</div>
            <div className="faq-answer">
              Data is considered reliable if it meets baseline criteria for data review, acquisition, and completeness. ICoMEC filters out low-credibility datasets by mandating that entries score between 3 and 5 on these parameters.
              <ul className="content-list" style={{ marginTop: "1rem" }}>
                <li><strong>Data Review:</strong> Internal, external, or third-party verification.</li>
                <li><strong>Data Acquisition:</strong> Methods used for collection (physical measurement vs. modeling).</li>
                <li><strong>Completeness:</strong> The degree of granularity and transparency provided.</li>
              </ul>
              For further details, you may refer to this publication.
            </div>
          </div>

          <div className="faq-item">
            <div className="faq-question">What is "Fit for Purpose" data?</div>
            <div className="faq-answer">
              Different stakeholders require different types of information. "Fit for purpose" means the platform provides data that aligns with the user's specific goal, scope, and required level of reporting detail.
            </div>
          </div>

          <div className="faq-item">
            <div className="faq-question">What is the difference between Generic and Defined Specificity?</div>
            <div className="faq-answer">
              <ul className="content-list" style={{ marginTop: "0.5rem" }}>
                <li><strong>Generic Specificity:</strong> Reliable data representing a broad range of timeframes, geographies, technologies, or manufacturers.</li>
                <li><strong>Defined Specificity:</strong> Reliable data that matches the user's specific requirements for time, location, technology, and manufacturer.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="faq-section">
          <h3>Methodology and Results</h3>
          
          <div className="faq-item">
            <div className="faq-question">How is uncertainty accounted for?</div>
            <div className="faq-answer">
              ICoMEC manages uncertainty by pre-selecting only credible, "reliable" datasets and utilizing a 95% confidence interval. Uncertainty is not expressed as a percentage or a standalone factor; it is embedded in the data filtration and statistical selection process.
            </div>
          </div>

          <div className="faq-item">
            <div className="faq-question">What is a "Representative Value"?</div>
            <div className="faq-answer">
              This is a statistical derivation based on the user's input criteria. It represents the most appropriate (here, conservative) number for the selected materials within the established sample size.
            </div>
          </div>

          <div className="faq-item">
            <div className="faq-question">What result will I receive?</div>
            <div className="faq-answer">
              The platform provides a representative embodied carbon value, which is either the upper bound of the 95% confidence interval or the maximum value of the dataset, depending on the sample size. These results update in real-time as input criteria change or as the database grows.
            </div>
          </div>

          <div className="faq-item">
            <div className="faq-question">How can I use these results?</div>
            <div className="faq-answer">
              Results can be downloaded via your user profile. They are intended for conducting building-level LCAs or comparing environmental impacts between material alternatives.
            </div>
          </div>

          <div className="faq-item">
            <div className="faq-question">Do results comply with green certification requirements?</div>
            <div className="faq-answer">
              Users are responsible for how they apply ICoMEC data. For specific certification purposes, we recommend consulting with the relevant green rating program or certification body.
            </div>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-main">
          <div className="footer-left">
            <h2>ICoMEC</h2>
            <p>
              Indian Construction Materials Embodied
              <br /> Carbon Data Base
            </p>
          </div>

          <div className="footer-links">
            <div>
              <h4>About Us</h4>
              <ul>
                <li><Link to="/who-we-are">Who We Are</Link></li>
                <li><Link to="/what-we-do">What We Do</Link></li>
                <li><Link to="/leadership">Leadership</Link></li>
                <li><Link to="/partners">Partners</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/glossary">Glossary</Link></li>
              </ul>
            </div>
            <div>
              <h4>Resources</h4>
              <ul>
                {/* <li><Link to="/resource-library">Resource Library</Link></li> */}
                <li><Link to="/events">Events</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      <div className="footer-bottom">
        <p>
          <Link to="/cookies-policy">Cookies Policy</Link> · <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/legal">Legal</Link> · <Link to="/terms-of-use">Terms of Use</Link>
          {/* · <Link to="/imprint">Imprint</Link> */}
        </p>
        <p>© 2026 CARBSE. All rights reserved.</p>
      </div>
    </div>
  );
};

/** React component — FAQPage. */
export default FAQPage;
